import { createContext, createElement, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { getWsUrl } from '../lib/runtime-config';

type RealtimeOverride = {
  entityId: string;
  state?: string;
  attributes?: Record<string, unknown>;
  lastChanged?: string;
  lastUpdated?: string;
};

type RealtimeContextValue = {
  status: 'connecting' | 'connected' | 'disconnected';
  overrides: Record<string, RealtimeOverride>;
};

const RealtimeContext = createContext<RealtimeContextValue>({
  status: 'connecting',
  overrides: {}
});

export function applyRealtimeOverride<T extends { entity_id?: string; entityId?: string; state?: string; attributes?: Record<string, unknown>; lastKnownState?: string }>(
  device: T,
  overrides: Record<string, RealtimeOverride>
): T {
  const entityId = device.entity_id || device.entityId;
  if (!entityId) return device;
  const override = overrides[entityId];
  if (!override) return device;

  return {
    ...device,
    state: override.state ?? device.state,
    lastKnownState: override.state ?? device.lastKnownState,
    attributes: override.attributes ? { ...(device.attributes || {}), ...override.attributes } : device.attributes
  };
}

export function RealtimeProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<'connecting' | 'connected' | 'disconnected'>('connecting');
  const [overrides, setOverrides] = useState<Record<string, RealtimeOverride>>({});
  const retryRef = useRef<number | null>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const reconnectDelayRef = useRef(1000);

  useEffect(() => {
    let stopped = false;

    const clearRetry = () => {
      if (retryRef.current !== null) {
        window.clearTimeout(retryRef.current);
        retryRef.current = null;
      }
    };

    const scheduleReconnect = () => {
      clearRetry();
      if (stopped) return;
      const delay = reconnectDelayRef.current;
      reconnectDelayRef.current = Math.min(reconnectDelayRef.current * 1.6, 5000);
      retryRef.current = window.setTimeout(connect, delay);
    };

    const connect = () => {
      if (stopped) return;
      setStatus('connecting');

      try {
        const ws = new WebSocket(getWsUrl());
        socketRef.current = ws;

        ws.onopen = () => {
          reconnectDelayRef.current = 1000;
          setStatus('connected');
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(String(event.data || '{}'));
            if (data?.type !== 'ha_state_changed') return;
            const payload = data.payload || {};
            const newState = payload.new_state || {};
            const entityId = String(newState.entity_id || payload.entity_id || '');
            if (!entityId) return;

            setOverrides((current) => ({
              ...current,
              [entityId]: {
                entityId,
                state: typeof newState.state === 'string' ? newState.state : undefined,
                attributes: (newState.attributes || {}) as Record<string, unknown>,
                lastChanged: typeof newState.last_changed === 'string' ? newState.last_changed : undefined,
                lastUpdated: typeof newState.last_updated === 'string' ? newState.last_updated : undefined
              }
            }));
          } catch {
            // ignore malformed frames
          }
        };

        ws.onclose = () => {
          socketRef.current = null;
          setStatus('disconnected');
          scheduleReconnect();
        };

        ws.onerror = () => {
          setStatus('disconnected');
          try { ws.close(); } catch {}
        };
      } catch {
        setStatus('disconnected');
        scheduleReconnect();
      }
    };

    connect();

    return () => {
      stopped = true;
      clearRetry();
      try { socketRef.current?.close(); } catch {}
      socketRef.current = null;
    };
  }, []);

  const value = useMemo(() => ({ status, overrides }), [status, overrides]);
  return createElement(RealtimeContext.Provider, { value }, children);
}

export function useRealtime() {
  return useContext(RealtimeContext);
}
